import {
  X as CloseIcon,
  MessagesSquare,
  Smartphone,
} from "@tamagui/lucide-icons";
import { Platform } from "react-native";
import { Sheet, XStack, YStack, useMedia } from "tamagui";

import { Button } from "@/components/Button";
import { Dialog } from "@/components/Dialog";
import { Header } from "@/components/Layout";
import { H3, H4, Paragraph } from "@/components/Typography";

interface RevokeSmsConsentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmRevoke: () => void;
  isLoading: boolean;
}

export default function RevokeSmsConsentDialog({
  open,
  onOpenChange,
  onConfirmRevoke,
  isLoading,
}: RevokeSmsConsentDialogProps) {
  const { lg } = useMedia();

  const dialogContent = (
    <YStack gap="$200">
      <Header>
        <H3>Are you sure you want to stop 2-way texting?</H3>
      </Header>
      <Paragraph>
        Keeping it on allows you to receive full messages from Curai over text,
        and reply directly by text.
      </Paragraph>
      <Paragraph>
        If you turn it off, you can still use Curai — just through the web app
        instead.
      </Paragraph>
      <Header>
        <H4>What changes if you turn off SMS?</H4>
      </Header>
      <XStack gap="$200" alignItems="flex-start">
        <Smartphone size={24} color="$textSecondary" flexShrink={0} />
        <Paragraph size="$sm">
          You won't be able to text directly with Curai, or receive full care
          messages via text.
        </Paragraph>
      </XStack>
      <XStack gap="$200" alignItems="flex-start">
        <MessagesSquare size={24} color="$textSecondary" flexShrink={0} />
        <Paragraph size="$sm">
          You can continue your care, message providers, and get support by
          logging into the web app.
        </Paragraph>
      </XStack>
      <YStack marginTop="$300">
        <Dialog.Close displayWhenAdapted asChild>
          <Button mode="primary" onPress={() => onOpenChange(false)}>
            Continue SMS
          </Button>
        </Dialog.Close>
        <Button
          mode="tertiary"
          loading={isLoading}
          onPress={onConfirmRevoke}
          disabled={isLoading}
        >
          Stop SMS
        </Button>
      </YStack>
    </YStack>
  );

  return (
    <Dialog modal open={open} onOpenChange={onOpenChange}>
      <Dialog.Adapt when={!lg}>
        <Sheet
          modal
          dismissOnSnapToBottom
          snapPointsMode="fit"
          moveOnKeyboardChange
        >
          <Sheet.Frame>
            <XStack
              justifyContent="flex-end"
              alignItems="center"
              padding="$200"
            >
              <Dialog.Close displayWhenAdapted asChild>
                <Button.Wrapper aria-label="Close">
                  <CloseIcon />
                </Button.Wrapper>
              </Dialog.Close>
            </XStack>
            <YStack padding="$200">{dialogContent}</YStack>
          </Sheet.Frame>
          <Sheet.Overlay
            backgroundColor="$fillOverlay"
            style={{
              backdropFilter: "blur(8px)",
            }}
          />
        </Sheet>
      </Dialog.Adapt>

      <Dialog.Portal>
        <Dialog.Overlay
          backgroundColor="$fillOverlay"
          style={Platform.select({
            web: {
              backdropFilter: "blur(8px)",
            },
          })}
        />
        <Dialog.FocusScope trapped loop focusOnIdle={300}>
          <Dialog.Content padding={0} width={500}>
            <YStack
              padding="$200"
              width="100%"
              height="100%"
              gap="$100"
              borderTopLeftRadius="$150"
              borderTopRightRadius="$150"
              borderBottomLeftRadius="$150"
              borderBottomRightRadius="$150"
            >
              <XStack
                justifyContent="flex-end"
                alignItems="center"
                marginBottom="$100"
              >
                <Dialog.Close asChild>
                  <Button.Wrapper aria-label="Close">
                    <CloseIcon />
                  </Button.Wrapper>
                </Dialog.Close>
              </XStack>
              {dialogContent}
            </YStack>
          </Dialog.Content>
        </Dialog.FocusScope>
      </Dialog.Portal>
    </Dialog>
  );
}
